import { crud } from '@/common/crud';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { useAuthStore } from '@/stores/auth';

import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getUrlFromCanonical, jsonify } from '@/common/core';
import { useDevOnly } from '@/composables/useDevOnly';
import { Task } from '@/fhirworks';
import { addDays } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';

const { isProfile, isBnSupport } = useDevOnly();
const canSeeDraftForms = () => isBnSupport() || isProfile();

const addTask = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const props = { type: 'task', title: data.title || 'Task' };
    const task = {
        id: data.id || uuidv4(),
        resourceType: 'Task',
        owner: data.owner,
        for: data.for,
        encounter: data.encounter,
        parentGroupTask: data.parentGroupTask,
    };

    // determine required assignments

    let assignee = data.assignee || [];

    // add an owner assignment if this is a patient task and the assignment doesn't already exist
    // does not apply to groupTasks
    if (task.for && !assignee.some((item) => item.id === data.owner.id) && !data.groupParticipantTask && !data.noAssignment) {
        assignee.push(data.owner);
    }

    // set start/end dates
    props.start = data.start || undefined;
    props.end = data.end || addDays(new Date(), 7).toISOString();
    task.restriction = { period: { start: props.start, end: props.end } };

    // set additional properties
    task.authoredOn = new Date().toISOString();
    task.kanban = { priority: data.priority || Date.now().toString() };
    task.intent = data.intent || 'unknown';
    task.status = data.status || 'accepted';

    // set description if provided
    if (data.description) {
        task.description = data.description;
    }

    // attach appointment id to task if required
    if (!isEmpty(data.appointment) && !task.basedOn?.find((item) => item.id === data.appointment.id)) {
        task.basedOn = { id: data.appointment.id, resourceType: 'Appointment', display: data.appointment.code?.text };
    }

    // set task type, title
    if (data.form) {
        if (data.form.title && data.form.version && data.form.url && data.form.formType !== undefined) {
            const form = data.form;
            props.type = form.formType;
            props.title = form.title;
            props.version = form.url + '|' + form.version;
            if (!data.parentGroupTask) {
                task.focus = {
                    display: form.title,
                    resourceType: 'Questionnaire',
                };
            }

            if (props.type === 'live') {
                task.restriction = undefined;
                task.status = 'ready';
                assignee = [];
            }
        } else {
            const formInput = data.form?.url || data.form;

            const urlInfo = getUrlFromCanonical(formInput);
            let canonicalUrl = urlInfo.fullUrl;

            if (urlInfo.version) {
                canonicalUrl += '&_elements=title,formType,version,url';
            } else {
                const statusQuery = canSeeDraftForms() ? 'status=active,draft' : 'status=active';
                canonicalUrl += '&' + statusQuery + '&_elements=title,formType,version,url';
            }

            const res = await $httpFhirApi.get(canonicalUrl);
            const questionnaire = res.data[0];
            if (!questionnaire) return existingTransaction === false ? [] : transaction;

            props.type = questionnaire.formType;
            props.title = questionnaire.title;
            props.version = questionnaire.url + '|' + questionnaire.version;
            if (!data.parentGroupTask) {
                task.focus = {
                    display: questionnaire.title,
                    resourceType: 'Questionnaire',
                };
            }

            if (props.type === 'live') {
                task.restriction = undefined;
                task.status = 'ready';
                assignee = [];
            }
        }
    }

    task.code = { coding: [{ code: props.type, display: props.title, version: props.version }], text: props.title };

    // create as a subtask (example: group encounter participant form)
    if (data.groupParticipantTask && data.groupTask) {
        task.basedOn = [{ id: data.groupParticipantTask, resourceType: 'Task' }];
    }
    if (data.groupTask) {
        task.groupTask = data.groupTask;
    }

    // mark as new task so that comments get created
    task.temp = { isNew: true };

    // TODO: validate task definition

    transaction = await addTaskResource(
        {
            task: task,
            title: props.title,
            appointment: data.appointment,
            groupParticipantTask: data.groupParticipantTask,
        },
        transaction,
    );

    if (assignee.length) {
        transaction = await addSubtask(
            {
                task: task,
                title: props.title,
                assignee: assignee,
                appointment: data.appointment,
            },
            transaction,
        );
    }

    if (existingTransaction === false) {
        const { postTransaction } = useWorkflow();
        return await postTransaction(transaction);
    }

    return transaction;
};

const addTaskResource = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // create the task resource
    let task = new Task(data.task);
    // attach appointment id to task if required
    if (data.appointment?.id && !task.basedOn?.find((item) => item.id === data.appointment.id)) {
        task.basedOn = { id: data.appointment.id, resourceType: 'Appointment', display: data.appointment.code?.text };
    }
    // new task comment/note
    const comments = ['Task Created'];
    addComment([task, '<p>' + comments.join('</p><p>') + '</p>']);
    transaction.push(crud.post(task));

    if (existingTransaction === false) {
        const { postTransaction } = useWorkflow();
        return await postTransaction(transaction);
    }

    return transaction;
};

const addSubtask = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    let newTask = jsonify(data.task);
    const primaryTaskOwner = data.task.owner;
    let comments = [];
    // if there are subtasks then the parent task comment gets overwritten because of the order of operations in the transaction so we reset it here
    if (data.task?.temp?.isNew) {
        comments.push('Task Created');
    }
    data.assignee.forEach((item) => {
        // create a unique id/task for each assignment
        newTask.id = uuidv4();
        let task = new Task(newTask);
        // attach appointment ref as an encounter
        if (data.appointment?.id && !task.basedOn?.find((item) => item.id === data.appointment.id)) {
            task.basedOn = { id: data.appointment.id, resourceType: 'Appointment', display: data.appointment.description };
        }
        // group parent task if applicable
        if (newTask.parentGroupTask) {
            task.groupTask = newTask.parentGroupTask;
        }

        task.partOf = [{ resourceType: 'Task', display: task.for?.display || task.owner?.display, id: data.task.id }];
        task.owner = item;
        let role;

        // also set "author" for parent group note task.
        if (task.focus || newTask.parentGroupTask) {
            role = primaryTaskOwner.id === item.id ? 'author' : 'editor';
        } else {
            role = primaryTaskOwner.id === item.id ? 'responsible' : 'assist';
        }
        task.code = { coding: [{ code: 'assignment', display: 'Assignment', version: role }], text: 're: ' + data.title };

        comments.push('Subtask Created: ' + task.owner.display + ' - ' + task.code.text);

        transaction.push(crud.post(task));
    });

    // if this is a new task, add the subtask comments to the parent task before it gets posted
    if (comments.length && data.task?.temp?.isNew) {
        // find the parent task POST in the transaction and update it with subtask comments
        const parentTaskIndex = transaction.findIndex((entry) => entry.resource?.id === data.task.id && entry.resource?.resourceType === 'Task' && entry.request?.method === 'POST');
        if (parentTaskIndex !== -1) {
            addComment([transaction[parentTaskIndex].resource, '<p>' + comments.join('</p><p>') + '</p>']);
        }
    } else if (comments.length) {
        // for existing tasks, patch with the new comments
        let parentTask = new Task(data.task);
        addComment([parentTask, '<p>' + comments.join('</p><p>') + '</p>']);
        transaction.push(crud.patch(parentTask));
    }

    if (existingTransaction === false) {
        const { postTransaction } = useWorkflow();
        return await postTransaction(transaction);
    }

    return transaction;
};

// const saveTask = async (data, existingTransaction) => {
//     let transaction = existingTransaction || [];
//
//     if (Array.isArray(data.task)) {
//         data.task.forEach((task) => {
//             if (task.temp.isNew) {
//                 transaction.push(crud.post(task));
//             } else {
//                 transaction.push(crud.patch(task));
//             }
//         });
//         return { transaction: transaction };
//     }
//     if (data.task.temp.isNew) {
//         transaction.push(crud.post(data.task));
//     } else {
//         transaction.push(crud.patch(data.task));
//     }
//
//     if (existingTransaction === false) {
//         const { postTransaction } = useWorkflow();
//         return await postTransaction(transaction);
//     }
//
//     return transaction;
// };

const deleteTask = async (taskId, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    let tasks = await $httpFhirApi.get('Task?id=' + taskId + '&_revinclude=Task:part-of');
    // Loop through and create transaction to delete primary task and all assignments/documents if any

    // Must use a FOR so that the await being used internally will function properly
    for (const task of tasks.data) {
        /**
         * Delete 'focus' items associated with the task depending on their type. Initially we are only working with
         * QuestionnaireResponse as the focus so they will be deleted. As other 'focus' resources are defined this code will
         * need to be updated to determine if those resources should be deleted with the task.
         */
        const resourcesToDelete = ['QuestionnaireResponse'];
        if (task.focus?.id && resourcesToDelete.includes(task.focus?.resourceType)) {
            // We have a QuestionnaireResponse that needs to be deleted, look for corresponding
            // ClinicalImpression information and remove if found.
            if (task.code?.coding?.[0]?.code === 'form') {
                let clinicalImpressions = await $httpFhirApi.get('ClinicalImpression?subject=' + task.for.id + '&supportingInfo=' + task.focus.id);
                clinicalImpressions.data?.forEach((clinicalImpression) => {
                    transaction.push(crud.delete({ id: clinicalImpression.id, resourceType: 'ClinicalImpression' }));
                });
            }

            // Look for Consent resources linked to this QuestionnaireResponse
            // and their associated Provenance records
            let consents = await $httpFhirApi.get('Consent?questionnaireResponse=' + task.focus.id + '&_revinclude=Provenance:target');
            consents.data?.forEach((resource) => {
                if (resource.resourceType === 'Consent') {
                    transaction.push(crud.delete({ id: resource.id, resourceType: 'Consent' }));
                } else if (resource.resourceType === 'Provenance') {
                    transaction.push(crud.delete({ id: resource.id, resourceType: 'Provenance' }));
                }
            });

            transaction.push(crud.delete(task.focus));
        }
        // delete task
        transaction.push(crud.delete(task));
    }

    if (existingTransaction === false) {
        const { postTransaction } = useWorkflow();
        return await postTransaction(transaction);
    }

    return transaction;
};

const processModifiedTasks = async (taskArray, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (!Array.isArray(taskArray)) {
        taskArray = [taskArray];
    }

    let parentTask = taskArray.find((item) => !item.partOf.length);
    const comments = [];
    taskArray.forEach((task) => {
        if (task.temp.action === 'delete') {
            transaction.push(crud.delete(task));
            comments.push('Subtask Deleted: ' + task.owner.display + ' - ' + task.code.text);
        } else {
            if (task.temp?.isNew) {
                transaction.push(crud.post(task));
                if (task.partOf.length) {
                    comments.push('Subtask Created: ' + task.owner.display + ' - ' + task.code.text);
                } else {
                    comments.push('Task Created');
                }
            } else {
                transaction.push(crud.patch(task));
            }
        }
    });
    if (comments.length && parentTask) {
        // in order for the patch to work on new tasks we need to rebuild the Task
        parentTask = new Task(parentTask);
        addComment([parentTask, '<p>' + comments.join('</p><p>') + '</p>']);
        transaction.push(crud.patch(parentTask));
    }

    if (existingTransaction === false) {
        const { postTransaction } = useWorkflow();
        return await postTransaction(transaction);
    }

    return transaction;
};

const createTaskResource = async (data) => {
    const [task, generateId] = data;
    // create a uuid for this task
    if (generateId) {
        task.id = uuidv4();
    }
    // set task properties
    task.intent = task.intent || 'unknown';
    task.status = task.status || 'accepted';
    task.authoredOn = new Date().toISOString();
    task.kanban = { priority: Date.now().toString() };
    task.code = { coding: [{ code: task.type, display: task.typeDisplay, version: task.typeVersion }] };
    if (task.title) {
        task.code = { ...task.code, text: task.title };
    }
    // if dates passed in then update
    task.end = task.end || addDays(new Date(), 7).toISOString();
    if (task.start || task.end) {
        task.restriction = { period: { start: task.start, end: task.end } };
    }
    // remove unneeded props
    delete task.board;
    delete task.type;
    delete task.typeVersion;
    delete task.typeDisplay;
    delete task.title;
    delete task.start;
    delete task.end;
    // create empty task object
    const newTaskObj = new Task(task);
    // new object with ID so mark it as new so that data-provider will not try and PATCH it
    if (generateId) {
        newTaskObj.temp.isNew = true;
    }
    return newTaskObj;
};

const addComment = (data) => {
    const authStore = useAuthStore();
    const [task, comment] = data;
    // author details
    let author = authStore.practitioner;
    let authorDetail = {
        Reference: {
            resourceType: author.resourceType,
            id: author.id,
            display: author.fullName,
        },
    };
    // add comment to existing comments and return for saving
    let notes = task.note;
    notes.push({ author: authorDetail, text: comment, time: new Date().toISOString() });
    return notes;
};

// Save task(s) using FHIR transactions with JSON patch
const saveTask = async (task) => {
    try {
        if (Array.isArray(task)) {
            let transactionEntries = [];
            task.forEach((taskToSave) => {
                transactionEntries.push({
                    request: {
                        method: 'PATCH',
                        url: '/Task/' + taskToSave.id + '?_method=json-patch',
                    },
                    resource: taskToSave.resourceJsonPatch(),
                });
            });

            let transaction = {
                resourceType: 'Bundle',
                type: 'transaction',
                entry: transactionEntries,
            };
            return await $httpFhirApi.post('/', transaction);
        } else {
            let transaction = {
                resourceType: 'Bundle',
                type: 'transaction',
                entry: [
                    {
                        request: {
                            method: 'PATCH',
                            url: '/Task/' + task.id + '?_method=json-patch',
                        },
                        resource: task.resourceJsonPatch(),
                    },
                ],
            };
            return await $httpFhirApi.post('/', transaction);
        }
    } catch (error) {
        console.error('Error saving tasks:', error);
        throw error;
    }
};

export function useTaskFlow() {
    return { addTask, deleteTask, createTaskResource, addComment, processModifiedTasks, saveTask };
}
